import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { fetchStaff as fetchStaffAPI, fetchStaffById, deleteStaff as deleteStaffAPI, fetchProfile as fetchProfileAPI, createChat } from '../utils/api';
import { ADMIN_ROLE, HUMAN_RESOURCES_STAFF, getRoleClassName } from '../utils/roles';
import { mapErrorMessage } from '../utils/errorMapping';
import StaffDetailModal from './StaffDetailModal';
import CreateStaffModal from './CreateStaffModal';
import EditStaffModal from './EditStaffModal';
import './_UserTiles.css';
import './_Buttons.css';

const Staff = () => {
    const { setSelectedChat, setChatOpen } = useOutletContext();
    const [staff, setStaff] = useState([]);
    const [totalStaff, setTotalStaff] = useState(0);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const fetchStaff = async () => {
        try {
            const data = await fetchStaffAPI();
            setStaff(data.data || []);
            setTotalStaff(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                fetchStaff();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        loadData();
    }, []);

    const handleCardClick = async (staffId) => {
        try {
            const data = await fetchStaffById(staffId);
            setSelectedStaff(data.data);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleCloseModal = () => {
        setSelectedStaff(null);
    };

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
    };

    const handleStaffCreated = () => {
        fetchStaff();
    };

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedStaff(null);
    };

    const handleStaffUpdated = () => {
        fetchStaff();
    };

    const canManageStaff = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === HUMAN_RESOURCES_STAFF);

    const handleDeleteStaff = async () => {
        if (!selectedStaff) return;

        try {
            await deleteStaffAPI(selectedStaff.id);
            handleCloseModal();
            fetchStaff();
            setMessage('Staff member deleted successfully.');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleCreateChat = async (userId, member) => {
        try {
            const chat = await createChat(userId);
            if (chat && chat.data && (chat.data.id || chat.data.ID)) {
                const chatId = chat.data.id || chat.data.ID;
                const chatObject = { 
                    ...chat.data, 
                    id: chatId,
                    name: `${member.first_name} ${member.last_name}`
                };
                setSelectedChat(chatObject);
                setChatOpen(true);
            }
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    return (
        <div className="students-container">
            <div className='user-tiles'>
                <h1>Staff | {totalStaff}</h1>
                {canManageStaff && (
                    <button onClick={handleOpenCreateModal} className="add-student-button">+</button>
                )}
            </div>
            {error && <p className="error">{error}</p>}
            {message && <p className="message">{message}</p>}
            <div className="user-tiles-container">
                {staff.map(member => (
                    <div key={member.id} className="user-tile" onClick={() => handleCardClick(member.id)}>
                        <h2>{member.first_name} {member.last_name}</h2>
                        <p>{member.email}</p>
                        <button onClick={(e) => { e.stopPropagation(); handleCreateChat(member.id, member); }} className="chat-button-card">💬</button>
                        {member.roles && (
                            <div className="roles-container">
                                {member.roles.map(role => (
                                    <div key={role.id} className={`role-tile ${getRoleClassName(role.name)}`}>{role.name}</div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {selectedStaff && !isEditModalOpen && (
                <StaffDetailModal
                    staff={selectedStaff}
                    onClose={handleCloseModal}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteStaff}
                    canManage={canManageStaff}
                />
            )}
            {isCreateModalOpen && (
                <CreateStaffModal
                    onClose={handleCloseCreateModal}
                    onStaffCreated={handleStaffCreated}
                />
            )}
            {isEditModalOpen && selectedStaff && (
                <EditStaffModal
                    staff={selectedStaff}
                    onClose={handleCloseEditModal}
                    onStaffUpdated={handleStaffUpdated}
                />
            )}
        </div>
    );
};

export default Staff;